import { Observable, from } from "rxjs";
import { delay, filter, map, switchMap, tap } from "rxjs/operators";
import { TStateLens } from "../../../../domain/codecs/state";
import { formOperation } from "../../wrappers/formOperation";
import { TCodecSetState, TGetCodecState } from "../applyActions";
import { routeAction, action } from "./actionFunctions";
import { TActionsDefinitionsList } from "./TAction";
import { foldForm } from "../../functions/form/foldForm";
import { constants } from "../../../../shared/src/regexes";
import { validationErrorCodeConstants } from "../../../../shared/src/validation/ErrorCode";
import { TChatsMessageForm, UpdateChatMessageForm, SetChatMessagePinnedForm } from "../../../../domain/codecs/form/ChatsMessageForm";
import { runAutomaticUpdates } from "./automaticBlockUpdate";

export const actions: TActionsDefinitionsList = [
    routeAction("VIEW_CRM_LEGAL_CASE", (obs$, lens, setState, getState) => {
        obs$.pipe(
            tap((action) => setState(lens.case_timeline_block.edited.case_id.set(action.params.caseId))),
        ).subscribe();
    }),

    action("LEGAL_CASE_GET_TIMELINE", (obs$, lens, setState, getState) => {
        obs$.pipe(
            $loadTimeline(lens, setState, getState),
        ).subscribe();
    }),

    action(
        "LEGAL_CASE_TIMELINE_TYPE_MESSAGE",
        (obs$, lens, setState, getState) =>
            obs$.pipe(
                tap((form) => setState(lens.case_timeline_block.children.chat_message_form.set(form)))
            )
            .subscribe()
    ),

    action(
        "LEGAL_CASE_TIMELINE_SELECT_ROADBLOCK",
        (obs$, lens, setState, getState) =>
            obs$.pipe(
                map((target) => {
                    const currentForm = lens.case_timeline_block.children.chat_message_form.get()(getState());
                    return {
                        ...currentForm,
                        edited: {
                            ...currentForm.edited,
                            chats_id: target.chats_id,
                            roadblock_id: target.roadblock_id,
                        },
                        original: {
                            ...currentForm.original,
                            chats_id: target.chats_id,
                            roadblock_id: target.roadblock_id,
                        },
                    };
                }),
                tap((form) => setState(lens.case_timeline_block.children.chat_message_form.set(form)))
            )
            .subscribe()
    ),

    action(
        "LEGAL_CASE_TIMELINE_SUBMIT_MESSAGE",
        (obs$, lens, setState, getState, routes, dispatch) =>
            obs$.pipe(
                map(foldForm<TChatsMessageForm, TChatsMessageForm>(
                    (form) => constants.LETTERS.test(form.edited.note_body.replace(constants.HTML, "")),
                    (form) => ({
                        ...form,
                        status: "validationError",
                        validationErrors: [
                            [validationErrorCodeConstants.NOTHING_TO_SEND_VALIDATION, ""]
                        ]
                    }),
                    (form) => ({
                        ...form,
                        status: "submitting",
                    }),
                )),
                tap((form) => setState(lens.case_timeline_block.children.chat_message_form.set(form))),

                filter((form) => form.status === "submitting"),
                switchMap((form) =>
                    from(formOperation("CreateChatMessage", form)).pipe(
                        tap((response) => {
                            setState(
                                lens.case_timeline_block.children.chat_message_form.set({
                                    ...form,
                                    status: response.status
                                })
                            )
                        }),

                        filter((response) => response.status === "success"),
                        $reloadTimelineSilently(lens, setState, getState),
                    ),
                ),
                tap(() => runAutomaticUpdates(routes, dispatch, getState, "case_roadblocks"))
            )
            .subscribe()
    ),

    action(
        "LEGAL_CASE_TIMELINE_EDIT_NOTE",
        (obs$, lens, setState, getState) =>
            obs$.pipe(
                map((payload) => ({
                    ...UpdateChatMessageForm.newDefault(),
                    edited: { id: payload.id, chats_id: payload.chats_id, note_body: payload.note_body },
                    status: "submitting" as const,
                })),
                switchMap((form) =>
                    from(formOperation("UpdateChatMessage", form)).pipe(
                        filter((response) => response.status === "success"),
                        $reloadTimelineSilently(lens, setState, getState),
                    ),
                ),
            )
            .subscribe()
    ),

    action(
        "LEGAL_CASE_TIMELINE_PIN_NOTE",
        (obs$, lens, setState, getState) =>
            obs$.pipe(
                map((payload) => ({
                    ...SetChatMessagePinnedForm.newDefault(),
                    edited: { id: payload.id, chats_id: payload.chats_id, pinned: payload.pinned },
                    status: "submitting" as const,
                })),
                switchMap((form) =>
                    from(formOperation("SetChatMessagePinned", form)).pipe(
                        filter((response) => response.status === "success"),
                        $reloadTimelineSilently(lens, setState, getState),
                    ),
                ),
            )
            .subscribe()
    ),
];

const $loadTimeline = <T>(lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) =>
    (obs$: Observable<T>) =>
        obs$.pipe(
            tap(() => setState(lens.case_timeline_block.status.set("loading"))),
            switchMap(() => formOperation("GetCaseTimeline", lens.case_timeline_block.get()(getState()))),
            tap((timeline) => setState(lens.case_timeline_block.set({ ...timeline, status: "success" }))),
            delay(500),
            tap(() => setState(lens.case_timeline_block.status.set("untouched"))),
        )
;

const $reloadTimelineSilently = <T>(lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) =>
    (obs$: Observable<T>) =>
        obs$.pipe(
            switchMap(() => formOperation("GetCaseTimeline", lens.case_timeline_block.get()(getState()))),
            tap((timeline) => setState(lens.case_timeline_block.set({ ...timeline, status: "success" }))),
        )
;
